import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const getSocketChat = () => import("../../../utils/SocketChat");

let storeInstance;
export const setStore = (store) => {
  storeInstance = store;
};

export const chatApi = createApi({
  reducerPath: "chatApi",
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_API + "services/chat",
    prepareHeaders: (headers, { getState }) => {
      const token = getState()?.userSlice?.token;
      if (token) headers.set("Authorization", `Bearer ${token}`);
      return headers;
    },
  }),

  endpoints: (builder) => ({
    getChats: builder.query({
      query: () => ({ url: `/`, method: "GET" }),
      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        try {
          await cacheDataLoaded;
        } catch {
          return;
        }

        const { onNewMessage, onChatNotification } = await getSocketChat();

        const unsubscribeNew = onNewMessage((socketPayload) => {
          updateCachedData((draft) => {
            const { chatId, chat } = socketPayload;
            if (!chat) return;

            const chatIndex = draft?.data?.findIndex((c) => c._id === chatId);
            if (chatIndex === -1) return;

            draft.data[chatIndex] = {
              ...draft.data[chatIndex],
              lastMessage: chat.lastMessage,
              lastMessageAt: chat.lastMessageAt,
              messageCount: chat.messageCount,
              unreadCount: chat.unreadCount,
            };

            const [moved] = draft.data.splice(chatIndex, 1);
            draft.data.unshift(moved);
          });
        });

        const unsubscribeNotification = onChatNotification((notification) => {
          updateCachedData((draft) => {
            const { chatId, messages, unreadCount } = notification;
            if (!chatId) return;

            const chatIndex = draft?.data?.findIndex((c) => c._id === chatId);
            if (chatIndex === -1) return;

            const chat = draft.data[chatIndex];
            if (unreadCount) chat.unreadCount = unreadCount;

            if (messages && messages.length > 0) {
              const lastMsg = messages[messages.length - 1];
              chat.lastMessage = {
                text: lastMsg.message || lastMsg.text || "",
                senderId: lastMsg.senderId,
              };
              chat.lastMessageAt = lastMsg.createdAt;
              chat.messageCount = (chat.messageCount || 0) + messages.length;
            }

            const [moved] = draft.data.splice(chatIndex, 1);
            draft.data.unshift(moved);
          });
        });

        await cacheEntryRemoved;
        unsubscribeNew?.();
        unsubscribeNotification?.();
      },
    }),

    getChatMessages: builder.query({
      query: ({ chatId }) => ({
        url: `/${chatId}/messages`,
        method: "GET",
      }),
      // 👇 THE FIX: destroy this cache entry immediately when the chat closes,
      // instead of keeping it alive for 60s. Guarantees a fresh onCacheEntryAdded
      // (and freshly bound socket listeners) every time the chat is reopened.
      keepUnusedDataFor: 0,
      async onCacheEntryAdded(
        { chatId },
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved, getState },
      ) {
        try {
          await cacheDataLoaded;
        } catch {
          return;
        }

        const { onNewMessage, onChatNotification, onMessagesRead } =
          await getSocketChat();

        const unsubscribeNew = onNewMessage((payload) => {
          if (payload.chatId !== chatId) return;
          updateCachedData((draft) => {
            const incomingMsg = payload.messages?.at(0) || payload.message;
            if (!incomingMsg?._id) return;
            if (draft.data.some((m) => m._id === incomingMsg._id)) return;

            const state = getState();
            const currentUserId =
              state.userSlice?.user?._id || state.userSlice?.user?.id;
            if (currentUserId && incomingMsg.senderId !== currentUserId) {
              if (!incomingMsg.seenBy) incomingMsg.seenBy = [];
              if (!incomingMsg.seenBy.includes(currentUserId)) {
                incomingMsg.seenBy.push(currentUserId);
              }
            }
            draft.data.push(incomingMsg);
          });
        });

        const unsubscribeNotificationMsgs = onChatNotification((payload) => {
          if (payload.chatId !== chatId) return;
          if (!payload.messages?.length) return;

          updateCachedData((draft) => {
            const state = getState();
            const currentUserId =
              state.userSlice?.user?._id || state.userSlice?.user?.id;

            payload.messages.forEach((incomingMsg) => {
              if (!incomingMsg?._id) return;
              if (draft.data.some((m) => m._id === incomingMsg._id)) return;

              if (currentUserId && incomingMsg.senderId !== currentUserId) {
                if (!incomingMsg.seenBy) incomingMsg.seenBy = [];
                if (!incomingMsg.seenBy.includes(currentUserId)) {
                  incomingMsg.seenBy.push(currentUserId);
                }
              }
              draft.data.push(incomingMsg);
            });
          });
        });

        const unsubscribeRead = onMessagesRead((payload) => {
          const { chatId: readChatId, seenBy } = payload;
          if (readChatId !== chatId) return;

          const state = getState();
          const currentUserId =
            state.userSlice?.user?._id || state.userSlice?.user?.id;

          updateCachedData((draft) => {
            draft.data.forEach((msg) => {
              if (msg.senderId === currentUserId && msg.status !== "read") {
                msg.status = "read";
                if (!msg.seenBy) msg.seenBy = [];
                if (seenBy && !msg.seenBy.includes(seenBy)) {
                  msg.seenBy.push(seenBy);
                }
              }
            });
          });
        });

        await cacheEntryRemoved;
        unsubscribeNew?.();
        unsubscribeNotificationMsgs?.();
        unsubscribeRead?.();
      },
    }),

    sendMessage: builder.mutation({
      query: ({ chatId, message, type, media, replyTo }) => ({
        url: `/${chatId}/messages`,
        method: "POST",
        body: {
          message,
          type: type || "text",
          media: media || [],
          replyTo: replyTo || null,
        },
      }),
      async onQueryStarted(
        { chatId, message },
        { dispatch, getState, queryFulfilled },
      ) {
        const state = getState();
        const currentUserId =
          state.userSlice?.user?._id || state.userSlice?.user?.id;

        const patchResult = dispatch(
          chatApi.util.updateQueryData("getChats", undefined, (draft) => {
            const chatIndex = draft?.data?.findIndex((c) => c._id === chatId);
            if (chatIndex === -1) return;

            const chat = draft.data[chatIndex];
            chat.lastMessage = {
              text: message,
              senderId: currentUserId,
            };
            chat.lastMessageAt = new Date().toISOString();
            chat.messageCount = (chat.messageCount || 0) + 1;

            const [moved] = draft.data.splice(chatIndex, 1);
            draft.data.unshift(moved);
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    getOneChat: builder.query({
      query: ({ id }) => {
        return {
          url: `/${id}`,
          method: "GET",
        };
      },
    }),

    createChat: builder.mutation({
      query: ({ offerId }) => ({
        url: `/new/${offerId}`,
        method: "POST",
      }),
    }),
  }),
});

export const {
  useGetChatsQuery,
  useGetChatMessagesQuery,
  useSendMessageMutation,
  useGetOneChatQuery,
  useCreateChatMutation,
} = chatApi;